<template>
  <div class="about-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="section-container">
        <h1 class="page-header__title">{{ t.title }}</h1>
        <div class="page-header__breadcrumb">
          <router-link to="/mira-solutions">Mira Solutions</router-link>
          <span>/</span>
          <span>{{ t.title }}</span>
        </div>
      </div>
    </div>

    <!-- About Content -->
    <section class="section">
      <div class="section-container">
        <div class="about-intro animate-on-scroll">
          <div class="about-intro__content">
            <span class="badge-label">
              <span class="badge-label__dot"></span>
              {{ t.intro.badge }}
            </span>
            <h2>{{ t.intro.title.pre }}<span class="text-gradient">{{ t.intro.title.hl }}</span>{{ t.intro.title.post }}</h2>
            <p class="about-intro__lead">{{ t.intro.lead }}</p>
          </div>
        </div>

        <!-- Vision & Mission -->
        <div class="vm-grid animate-on-scroll">
          <div class="vm-card">
            <div class="vm-card__icon"><i class="fa fa-eye"></i></div>
            <h3>{{ t.vision.title }}</h3>
            <p>{{ t.vision.text }}</p>
          </div>
          <div class="vm-card vm-card--accent">
            <div class="vm-card__icon"><i class="fa fa-rocket"></i></div>
            <h3>{{ t.mission.title }}</h3>
            <p>{{ t.mission.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Us Section -->
    <section class="section section--alt">
      <div class="section-container">
        <div class="section-title animate-on-scroll">
          <span class="badge-label">
            <span class="badge-label__dot"></span>
            {{ t.values.badge }}
          </span>
          <h2>{{ t.values.title.pre }}<span class="text-gradient">{{ t.values.title.hl }}</span>{{ t.values.title.post }}</h2>
        </div>
        <div class="values-grid animate-on-scroll">
          <div class="value-card" v-for="value in values" :key="value.key">
            <div class="value-card__icon"><i :class="value.icon"></i></div>
            <h4>{{ value.title }}</h4>
            <p>{{ value.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section section--gradient">
      <div class="section-container">
        <div class="about-cta animate-on-scroll">
          <h2>{{ t.cta.title }}</h2>
          <p>{{ t.cta.text }}</p>
          <router-link to="/contact" class="btn-primary-custom" style="background: #fff; color: #6C2973;">{{ t.cta.button }}</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { localize } from '@/i18n'
import copy from '@/locales/about'

const VALUES = [
  { key: 'custom', icon: 'fa fa-check-circle' },
  { key: 'team', icon: 'fa fa-users' },
  { key: 'responsive', icon: 'fa fa-headphones' },
  { key: 'security', icon: 'fa fa-shield' },
  { key: 'results', icon: 'fa fa-line-chart' },
  { key: 'innovation', icon: 'fa fa-refresh' }
]

export default {
  name: 'AboutPage',
  computed: {
    t() {
      return copy[this.$lang]
    },
    values() {
      return localize(VALUES, this.t.values.items)
    }
  },
  mounted() {
    this.initScrollAnimations()
  },
  methods: {
    initScrollAnimations() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) { entry.target.classList.add('animated') }
          })
        },
        { threshold: 0.1 }
      )
      this.$nextTick(() => {
        document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el))
      })
    }
  }
}
</script>

<style scoped lang="scss">
.text-gradient {
  background: linear-gradient(135deg, #6C2973, #C73468);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.about-intro {
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;

  &__lead {
    font-size: 1.1rem;
    color: $text-body;
    margin-top: 16px;
    line-height: 1.8;
  }
}

.vm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.vm-card {
  background: #fff;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 24px rgba(108, 41, 115, 0.06);
  border: 1px solid rgba(108, 41, 115, 0.06);

  h3 {
    margin: 16px 0 12px;
  }

  p {
    color: $text-body;
    line-height: 1.8;
  }

  &--accent {
    background: linear-gradient(135deg, #6C2973, #C73468);

    h3, p { color: #fff; }

    .vm-card__icon {
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
    }
  }
}

.vm-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(108, 41, 115, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #6C2973;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.value-card {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(108, 41, 115, 0.06);
  transition: all 0.3s ease;
  border: 1px solid rgba(108, 41, 115, 0.06);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(108, 41, 115, 0.12);
  }

  h4 { margin: 16px 0 10px; }
  p { font-size: 0.9rem; color: $text-body; line-height: 1.7; }
}

.value-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(108, 41, 115, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #6C2973;
  transition: all 0.3s ease;
}

.value-card:hover .value-card__icon {
  background: linear-gradient(135deg, #6C2973, #C73468);
  color: white;
}

.about-cta {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;

  h2 { color: #fff; margin-bottom: 16px; }
  p { color: rgba(255, 255, 255, 0.85); margin-bottom: 28px; }
}

@media (max-width: 991px) {
  .vm-grid { grid-template-columns: 1fr; }
  .values-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 576px) {
  .values-grid { grid-template-columns: 1fr; }
}
</style>
